import { useQuery } from '@tanstack/react-query';
import { fetchFilesList } from '@/api/files';
import type { FilesListSearch } from '../-types';

export const filesListKeys = {
  all: ['files-list'] as const,
  list: (params: object) => ['files-list', params] as const,
};

function buildFilter(
  filters: Record<string, string[]>,
  quickFilter: string,
): string {
  const f: Record<string, string> = {};
  const name = filters.name?.[0];
  const type = filters.type?.[0];
  const uploadedBy = filters.updatedByUserName?.[0];
  if (name) f.name = name;
  if (type) f.type = type;
  if (uploadedBy) f.updatedByUserName = uploadedBy;
  if (quickFilter) f.fileContentType = quickFilter;
  return Object.keys(f).length ? JSON.stringify(f) : '';
}

function buildQs(search: FilesListSearch, filter: string): string {
  const qs = new URLSearchParams();
  qs.set('page', String(search.page));
  qs.set('count', String(search.count));
  if (search.sort) qs.set('sort', search.sort);
  if (filter) qs.set('filter', filter);
  qs.set('isArchived', 'false');
  return qs.toString();
}

export function useFilesList(search: FilesListSearch) {
  const filter = buildFilter(search.filters ?? {}, search.quickFilter);
  const qs = buildQs(search, filter);

  const query = useQuery({
    queryKey: filesListKeys.list({
      page: search.page,
      count: search.count,
      sort: search.sort,
      filter,
    }),
    queryFn: () => fetchFilesList(qs),
  });

  const files = query.data?.data ?? [];
  const meta = query.data?.meta;
  const totalPages =
    meta?.total && search.count ? Math.ceil(meta.total / search.count) : 0;

  return {
    files,
    meta,
    totalPages,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
  };
}
